import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createDepartment, getDepartmentById, updateDepartment } from '../../services/departmentService';
import { Department } from '../../types/models';
import { ArrowLeft, Save } from 'lucide-react';

const DepartmentForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const isEditMode = Boolean(id);
  
  const [formData, setFormData] = useState<Omit<Department, 'department_id' | 'created_at'>>({
    name: '',
    name_tamil: '',
    hidden_in_exports: false
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDepartment = async () => {
      if (id) {
        try {
          setLoading(true);
          const data = await getDepartmentById(id);
          if (data) {
            setFormData({
              name: data.name,
              name_tamil: data.name_tamil || '',
              hidden_in_exports: data.hidden_in_exports || false
            });
          }
        } catch (error) {
          console.error('Error fetching department:', error);
          setError('Failed to load department data');
        } finally {
          setLoading(false);
        }
      }
    };

    if (isEditMode) {
      fetchDepartment();
    }
  }, [id, isEditMode]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      
      if (isEditMode && id) {
        await updateDepartment(id, formData);
      } else {
        await createDepartment(formData);
      }
      
      navigate('/departments');
    } catch (error: any) {
      console.error('Error saving department:', error);
      setError(error.message || 'Failed to save department');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center">
        <button
          onClick={() => navigate(-1)}
          className="mr-4 inline-flex items-center p-2 border border-transparent rounded-full shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <ArrowLeft className="h-5 w-5" aria-hidden="true" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditMode ? t('departments.form.editTitle') : t('departments.form.addTitle')}
        </h1>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 gap-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                {t('departments.form.fields.name.label')}
              </label>
              <input
                type="text"
                name="name"
                id="name"
                required
                value={formData.name}
                onChange={handleChange}
                placeholder={t('departments.form.fields.name.placeholder')}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="name_tamil" className="block text-sm font-medium text-gray-700">
                {t('departments.form.fields.nameTamil.label')}
              </label>
              <input
                type="text"
                name="name_tamil"
                id="name_tamil"
                value={formData.name_tamil}
                onChange={handleChange}
                placeholder={t('departments.form.fields.nameTamil.placeholder')}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="hidden_in_exports"
                  name="hidden_in_exports"
                  type="checkbox"
                  checked={formData.hidden_in_exports}
                  onChange={handleChange}
                  className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="hidden_in_exports" className="font-medium text-gray-700">
                  {t('departments.form.fields.hideInExports.label')}
                </label>
                <p className="text-gray-500">
                  {t('departments.form.fields.hideInExports.description')}
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => navigate('/departments')}
              className="mr-3 py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {t('departments.actions.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400"
            >
              <Save className="h-4 w-4 mr-2" />
              {loading ? t('departments.actions.saving') : t('departments.actions.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DepartmentForm;